import React from 'react';

import { CustomOperatorValue } from '@aline/common';
import type {
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { getGridStringOperators } from '@mui/x-data-grid-premium';
import type {
  ContractTypeOption,
  LabelFilterOption,
  OwnerFilterOption,
  PartyFilterOption,
} from 'src/components';
import {
  ContractTypeAutocompleteFilter,
  LabelAutocompleteFilter,
  OwnerAutocompleteFilter,
  PartiesAutocompleteFilter,
  WorkflowSigningStatusAutocompleteFilter,
  WorkflowStatusAutocompleteFilter,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { getIsDocumentStatus } from 'src/helpers';

import { BoardAutocompleteFilter } from './BoardAutocompleteFilter';
import { ColumnAutocompleteFilter } from './ColumnAutocompleteFilter';
import { ColumnCategoryAutocompleteFilter } from './ColumnCategoryAutocompleteFilter';
import { DocumentStatusFilterAutoComplete } from './DocumentStatusFilterAutoComplete';
import { RowStatusAutocompleteFilter } from './RowStatusAutocompleteFilter';
import type { WorkflowAssigneeFilterOption } from './WorkflowAssignessFilter';
import { WorkflowAssigneeAutocompleteFilter } from './WorkflowAssignessFilter';
import { WorkflowPriorityAutocompleteFilter } from './WorkflowPriorityAutocompleteFilter';

function defineGridFilterOperator<TValue>(
  operator: GridFilterOperator<GridValidRowModel, TValue>
): GridFilterOperator {
  return operator;
}

type ObjectWithId = {
  id?: string | null;
};

type WorkflowAssigneeCellValue = {
  account?: ObjectWithId | null;
  isAssignee?: boolean | null;
};

function getObjectId(value: ObjectWithId | null | undefined): string | null {
  return typeof value?.id === 'string' ? value.id : null;
}

function getContractTypeId(value: ContractTypeOption | null | undefined): string | null {
  return value?.__typename === 'ContractType' ? value.id : null;
}

function getDocumentStatusValue(
  value: Apollo.DocumentStatus | null | undefined
): Apollo.DocumentStatus {
  return getIsDocumentStatus(value) ? value : Apollo.DocumentStatus.Draft;
}

function getIsMatchingWorkflowAssignee(value: WorkflowAssigneeCellValue, assigneeIds: Set<string>) {
  if (value.isAssignee !== true) return false;
  const accountId = getObjectId(value.account);
  return Boolean(accountId && assigneeIds.has(accountId));
}

interface ContractTypeFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.ContractType
    | CustomOperatorValue.ContractTypeIsEmpty
    | CustomOperatorValue.ContractTypeIsNotEmpty
    | CustomOperatorValue.ContractTypeNotAnyOf;
  value?: ContractTypeOption[];
}

function getIsContractTypeFilterItem(item: GridFilterItem): item is ContractTypeFilterItem {
  return (
    item.operator === CustomOperatorValue.ContractType ||
    item.operator === CustomOperatorValue.ContractTypeIsEmpty ||
    item.operator === CustomOperatorValue.ContractTypeIsNotEmpty ||
    item.operator === CustomOperatorValue.ContractTypeNotAnyOf
  );
}

export const contractTypeAnyOfFilterOperator = defineGridFilterOperator<ContractTypeOption | null>({
  InputComponent: (props: GridFilterInputValueProps) => (
    <ContractTypeAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsContractTypeFilterItem(filterItem)
    )
      return null;

    const filterTypeIds = new Set(
      filterItem.value.flatMap(contractType => {
        return contractType.__typename === 'ContractType' ? [contractType.id] : [];
      })
    );

    return value => {
      // Contract type autocomplete is empty.  All rows match. Note: Order *matters* here. Keep at top.
      if (filterTypeIds.size === 0) return true;
      // Filter is not empty.  Test cell value against filter.
      const contractTypeId = getContractTypeId(value);
      if (!contractTypeId) return false;

      return filterTypeIds.has(contractTypeId);
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.ContractType,
});

export const contractTypeIsEmptyFilterOperator =
  defineGridFilterOperator<ContractTypeOption | null>({
    InputComponent: () => null,
    getApplyFilterFn: () => {
      return value => {
        // Cell has no contract type.
        return !value;
      };
    },
    label: 'is empty',
    value: CustomOperatorValue.ContractTypeIsEmpty,
  });

export const contractTypeIsNotEmptyFilterOperator =
  defineGridFilterOperator<ContractTypeOption | null>({
    InputComponent: () => null,
    getApplyFilterFn: () => {
      return value => {
        // Contract type is not empty.
        return Boolean(value);
      };
    },
    label: 'is not empty',
    value: CustomOperatorValue.ContractTypeIsNotEmpty,
  });

export const contractTypeNotAnyOfFilterOperator =
  defineGridFilterOperator<ContractTypeOption | null>({
    InputComponent: (props: GridFilterInputValueProps) => (
      <ContractTypeAutocompleteFilter {...props} />
    ),
    getApplyFilterFn: filterItem => {
      if (
        !filterItem.field ||
        !filterItem.value ||
        !filterItem.operator ||
        !getIsContractTypeFilterItem(filterItem)
      )
        return null;

      const filterTypeIds = new Set(
        filterItem.value.flatMap(contractType => {
          return contractType.__typename === 'ContractType' ? [contractType.id] : [];
        })
      );

      return value => {
        // Contract type autocomplete is empty.  All rows match. Note: Order *matters* here. Keep at top.
        if (filterTypeIds.size === 0) return true;
        // Filter is not empty.  Test cell value against filter.
        const contractTypeId = getContractTypeId(value);
        if (!contractTypeId) return true;

        return !filterTypeIds.has(contractTypeId);
      };
    },
    label: 'is none of',
    value: CustomOperatorValue.ContractTypeNotAnyOf,
  });

// The order of these operators is what shows up in the UI.
export const allContractTypeFilters = [
  contractTypeAnyOfFilterOperator,
  contractTypeNotAnyOfFilterOperator,
  contractTypeIsEmptyFilterOperator,
  contractTypeIsNotEmptyFilterOperator,
];

interface PartyFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.Party;
  value?: PartyFilterOption[];
}

function getIsPartyFilterItem(item: GridFilterItem): item is PartyFilterItem {
  return (
    item.operator === CustomOperatorValue.Party ||
    item.operator === CustomOperatorValue.PartyIsEmpty ||
    item.operator === CustomOperatorValue.PartyIsNotEmpty ||
    item.operator === CustomOperatorValue.PartyNotAnyOf
  );
}

export const partyAnyOfFilterOperator = defineGridFilterOperator<PartyFilterOption[] | null>({
  InputComponent: (props: GridFilterInputValueProps) => <PartiesAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsPartyFilterItem(filterItem)
    )
      return null;

    const filterPartyIds = new Set(
      filterItem.value.flatMap(filterParty => {
        return filterParty.id ? [filterParty.id] : [];
      })
    );

    return value => {
      // Parties autocomplete is empty.  All rows match.  Order matters, put this at the top.
      if (filterPartyIds.size === 0) return true;
      // We assume params.value is an array of Parties...
      return (value ?? []).some(cellParty => {
        const partyId = getObjectId(cellParty);
        return Boolean(partyId && filterPartyIds.has(partyId));
      });
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.Party,
});

export const partyIsEmptyFilterOperator = defineGridFilterOperator<PartyFilterOption[] | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // No parties in cell.
      return (value ?? []).length === 0;
    };
  },
  label: 'is empty',
  value: CustomOperatorValue.PartyIsEmpty,
});

export const partyIsNotEmptyFilterOperator = defineGridFilterOperator<PartyFilterOption[] | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // Some parties in cell.
      return (value ?? []).length > 0;
    };
  },
  label: 'is not empty',
  value: CustomOperatorValue.PartyIsNotEmpty,
});

export const partyNotAnyOfFilterOperator = defineGridFilterOperator<PartyFilterOption[] | null>({
  InputComponent: (props: GridFilterInputValueProps) => <PartiesAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsPartyFilterItem(filterItem)
    )
      return null;

    const filterPartyIds = new Set(
      filterItem.value.flatMap(filterParty => {
        return filterParty.id ? [filterParty.id] : [];
      })
    );

    return value => {
      if (filterPartyIds.size === 0) return true;

      const cellHasSomeParty = (value ?? []).some(cellParty => {
        const partyId = getObjectId(cellParty);
        return Boolean(partyId && filterPartyIds.has(partyId));
      });

      return !cellHasSomeParty;
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.PartyNotAnyOf,
});

// The order of these operators is what shows up in the UI.
export const allPartyFilters = [
  partyAnyOfFilterOperator,
  partyNotAnyOfFilterOperator,
  partyIsEmptyFilterOperator,
  partyIsNotEmptyFilterOperator,
];

export interface OwnerFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.Owner | CustomOperatorValue.OwnerNotAnyOf;
  value?: OwnerFilterOption[];
}

export function getIsOwnerFilterItem(item: GridFilterItem): item is OwnerFilterItem {
  return (
    item.operator === CustomOperatorValue.Owner ||
    item.operator === CustomOperatorValue.OwnerNotAnyOf
  );
}

export const ownerAnyOfFilterOperator = defineGridFilterOperator<OwnerFilterOption | null>({
  InputComponent: (props: GridFilterInputValueProps) => <OwnerAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsOwnerFilterItem(filterItem)
    )
      return null;

    const filterOwnerIds = new Set(
      filterItem.value.flatMap(filterOwner => {
        return filterOwner.id ? [filterOwner.id] : [];
      })
    );

    return value => {
      // Owner autocomplete is empty.  All rows match.  Order matters, put at the top.
      if (filterOwnerIds.size === 0) return true;
      // We assume params.value is an owner object...
      const ownerId = getObjectId(value);
      if (!ownerId) return false;

      return filterOwnerIds.has(ownerId);
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.Owner,
});

export const ownerNotAnyOfFilterOperator = defineGridFilterOperator<OwnerFilterOption | null>({
  InputComponent: (props: GridFilterInputValueProps) => <OwnerAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsOwnerFilterItem(filterItem)
    )
      return null;

    const filterOwnerIds = new Set(
      filterItem.value.flatMap(filterOwner => {
        return filterOwner.id ? [filterOwner.id] : [];
      })
    );

    return value => {
      if (filterOwnerIds.size === 0) return true;
      const ownerId = getObjectId(value);
      if (!ownerId) return true;

      return !filterOwnerIds.has(ownerId);
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.OwnerNotAnyOf,
});

// The order of these operators is what shows up in the UI.
export const allOwnerFilters = [ownerAnyOfFilterOperator, ownerNotAnyOfFilterOperator];

interface WorkflowStateFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.WorkflowState;
  value?: Apollo.WorkflowState[];
}

function getIsWorkflowStateFilterItem(item: GridFilterItem): item is WorkflowStateFilterItem {
  return item.operator === CustomOperatorValue.WorkflowState;
}

export function getIsWorkflowStateEmpty(value: Apollo.WorkflowState | null) {
  return !value;
}

export const workflowStateAnyOfFilterOperator =
  defineGridFilterOperator<Apollo.WorkflowState | null>({
    InputComponent: (props: GridFilterInputValueProps) => (
      <WorkflowSigningStatusAutocompleteFilter {...props} />
    ),
    getApplyFilterFn: filterItem => {
      if (
        !filterItem.field ||
        !filterItem.value ||
        !filterItem.operator ||
        !getIsWorkflowStateFilterItem(filterItem)
      )
        return null;

      return value => {
        if (!Array.isArray(filterItem.value)) return false;
        // WorkflowState autocomplete is empty.  All rows match.
        if (!filterItem.value.length) return true;

        return filterItem.value.some(filterItemWorkflowState => filterItemWorkflowState === value);
      };
    },
    label: 'is any of',
    value: CustomOperatorValue.WorkflowState,
  });

export const workflowStateIsEmptyFilterOperator =
  defineGridFilterOperator<Apollo.WorkflowState | null>({
    InputComponent: () => null,
    getApplyFilterFn: () => getIsWorkflowStateEmpty,
    label: 'is empty',
    requiresFilterValue: false,
    value: CustomOperatorValue.WorkflowStateIsEmpty,
  });

// The order of these operators is what shows up in the UI.
export const allWorkflowStateFilters = [
  workflowStateAnyOfFilterOperator,
  workflowStateIsEmptyFilterOperator,
];

interface WorkflowPriorityFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.WorkflowPriority
    | CustomOperatorValue.WorkflowPriorityNotAnyOf
    | CustomOperatorValue.WorkflowPriorityIsEmpty
    | CustomOperatorValue.WorkflowPriorityIsNotEmpty;
  value?: Apollo.WorkflowPriority[];
}

function getIsWorkflowPriorityFilterItem(item: GridFilterItem): item is WorkflowPriorityFilterItem {
  return (
    item.operator === CustomOperatorValue.WorkflowPriority ||
    item.operator === CustomOperatorValue.WorkflowPriorityNotAnyOf ||
    item.operator === CustomOperatorValue.WorkflowPriorityIsEmpty ||
    item.operator === CustomOperatorValue.WorkflowPriorityIsNotEmpty
  );
}

export const workflowPriorityAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowPriorityAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsWorkflowPriorityFilterItem(filterItem)
    )
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value)) return false;
      if (!filterItem.value.length) return true;
      return filterItem.value.some(
        filterItemWorkflowPriority => filterItemWorkflowPriority === cellParams.value
      );
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.WorkflowPriority,
};

export const workflowPriorityNotAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowPriorityAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.operator || !getIsWorkflowPriorityFilterItem(filterItem))
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      return !filterItem.value.some(
        filterItemWorkflowPriority => filterItemWorkflowPriority === cellParams.value
      );
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.WorkflowPriorityNotAnyOf,
};

export const workflowPriorityIsEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !cellParams.value;
  },
  label: 'is empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowPriorityIsEmpty,
};

export const workflowPriorityIsNotEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !!cellParams.value;
  },
  label: 'is not empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowPriorityIsNotEmpty,
};

// The order of these operators is what shows up in the UI.
export const allWorkflowPriorityFilters = [
  workflowPriorityAnyOfFilterOperator,
  workflowPriorityNotAnyOfFilterOperator,
  workflowPriorityIsEmptyFilterOperator,
  workflowPriorityIsNotEmptyFilterOperator,
];

// Board filter operators
interface BoardFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.WorkflowBoard
    | CustomOperatorValue.WorkflowBoardNotAnyOf
    | CustomOperatorValue.WorkflowBoardIsEmpty
    | CustomOperatorValue.WorkflowBoardIsNotEmpty;
  value?: Array<{ __typename: 'Board'; id: string }>;
}

function getIsBoardFilterItem(item: GridFilterItem): item is BoardFilterItem {
  return (
    item.operator === CustomOperatorValue.WorkflowBoard ||
    item.operator === CustomOperatorValue.WorkflowBoardNotAnyOf ||
    item.operator === CustomOperatorValue.WorkflowBoardIsEmpty ||
    item.operator === CustomOperatorValue.WorkflowBoardIsNotEmpty
  );
}

export const boardAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => <BoardAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsBoardFilterItem(filterItem)
    )
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const filterBoardIds = new Set(filterItem.value.map(v => v.id));
      if (!cellParams.value || typeof cellParams.value !== 'object') return false;
      return filterBoardIds.has(cellParams.value.id);
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.WorkflowBoard,
};

export const boardNotAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => <BoardAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.operator || !getIsBoardFilterItem(filterItem)) return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const filterBoardIds = new Set(filterItem.value.map(v => v.id));
      if (!cellParams.value || typeof cellParams.value !== 'object') return true;
      return !filterBoardIds.has(cellParams.value.id);
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.WorkflowBoardNotAnyOf,
};

export const boardIsEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !cellParams.value;
  },
  label: 'is empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowBoardIsEmpty,
};

export const boardIsNotEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !!cellParams.value;
  },
  label: 'is not empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowBoardIsNotEmpty,
};

// The order of these operators is what shows up in the UI.
export const allBoardFilters = [
  boardAnyOfFilterOperator,
  boardNotAnyOfFilterOperator,
  boardIsEmptyFilterOperator,
  boardIsNotEmptyFilterOperator,
];

// Column (Status) filter operators
interface ColumnFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.WorkflowColumn
    | CustomOperatorValue.WorkflowColumnNotAnyOf
    | CustomOperatorValue.WorkflowColumnIsEmpty
    | CustomOperatorValue.WorkflowColumnIsNotEmpty;
  value?: Array<{ __typename: 'Column'; id: string }>;
}

function getIsColumnFilterItem(item: GridFilterItem): item is ColumnFilterItem {
  return (
    item.operator === CustomOperatorValue.WorkflowColumn ||
    item.operator === CustomOperatorValue.WorkflowColumnNotAnyOf ||
    item.operator === CustomOperatorValue.WorkflowColumnIsEmpty ||
    item.operator === CustomOperatorValue.WorkflowColumnIsNotEmpty
  );
}

export const columnAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => <ColumnAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsColumnFilterItem(filterItem)
    )
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const filterColumnIds = new Set(filterItem.value.map(v => v.id));
      if (!cellParams.value || typeof cellParams.value !== 'object') return false;
      return filterColumnIds.has(cellParams.value.id);
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.WorkflowColumn,
};

export const columnNotAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => <ColumnAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.operator || !getIsColumnFilterItem(filterItem))
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      const filterColumnIds = new Set(filterItem.value.map(v => v.id));
      if (!cellParams.value || typeof cellParams.value !== 'object') return true;
      return !filterColumnIds.has(cellParams.value.id);
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.WorkflowColumnNotAnyOf,
};

export const columnIsEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !cellParams.value;
  },
  label: 'is empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowColumnIsEmpty,
};

export const columnIsNotEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !!cellParams.value;
  },
  label: 'is not empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowColumnIsNotEmpty,
};

// The order of these operators is what shows up in the UI.
export const allColumnFilters = [
  columnAnyOfFilterOperator,
  columnNotAnyOfFilterOperator,
  columnIsEmptyFilterOperator,
  columnIsNotEmptyFilterOperator,
];

// Column Category (Status Type) filter operators
interface ColumnCategoryFilterItem extends GridFilterItem {
  operator:
    | 'isAnyOf'
    | 'isNoneOf'
    | CustomOperatorValue.WorkflowColumnCategoryIsEmpty
    | CustomOperatorValue.WorkflowColumnCategoryIsNotEmpty;
  value?: Apollo.ColumnCategory[];
}

function getIsColumnCategoryFilterItem(item: GridFilterItem): item is ColumnCategoryFilterItem {
  return (
    item.operator === 'isAnyOf' ||
    item.operator === 'isNoneOf' ||
    item.operator === CustomOperatorValue.WorkflowColumnCategoryIsEmpty ||
    item.operator === CustomOperatorValue.WorkflowColumnCategoryIsNotEmpty
  );
}

export const columnCategoryIsAnyOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <ColumnCategoryAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsColumnCategoryFilterItem(filterItem)
    )
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value)) return false;
      if (!filterItem.value.length) return true;
      return filterItem.value.some(filterCategory => filterCategory === cellParams.value);
    };
  },
  label: 'is any of',
  value: 'isAnyOf',
};

export const columnCategoryIsNoneOfFilterOperator: GridFilterOperator = {
  InputComponent: (props: GridFilterInputValueProps) => (
    <ColumnCategoryAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (!filterItem.field || !filterItem.operator || !getIsColumnCategoryFilterItem(filterItem))
      return null;

    return cellParams => {
      if (!Array.isArray(filterItem.value) || !filterItem.value.length) return true;
      return !filterItem.value.some(filterCategory => filterCategory === cellParams.value);
    };
  },
  label: 'is none of',
  value: 'isNoneOf',
};

export const columnCategoryIsEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !cellParams.value;
  },
  label: 'is empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowColumnCategoryIsEmpty,
};

export const columnCategoryIsNotEmptyFilterOperator: GridFilterOperator = {
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return cellParams => !!cellParams.value;
  },
  label: 'is not empty',
  requiresFilterValue: false,
  value: CustomOperatorValue.WorkflowColumnCategoryIsNotEmpty,
};

// The order of these operators is what shows up in the UI.
export const allColumnCategoryFilters = [
  columnCategoryIsAnyOfFilterOperator,
  columnCategoryIsNoneOfFilterOperator,
  columnCategoryIsEmptyFilterOperator,
  columnCategoryIsNotEmptyFilterOperator,
];

interface LabelFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.Label
    | CustomOperatorValue.LabelNotAnyOf
    | CustomOperatorValue.LabelIsEmpty
    | CustomOperatorValue.LabelIsNotEmpty;
  value?: LabelFilterOption[];
}

function getIsLabelFilterItem(item: GridFilterItem): item is LabelFilterItem {
  return (
    item.operator === CustomOperatorValue.Label ||
    item.operator === CustomOperatorValue.LabelNotAnyOf ||
    item.operator === CustomOperatorValue.LabelIsEmpty ||
    item.operator === CustomOperatorValue.LabelIsNotEmpty
  );
}

export const labelAnyOfFilterOperator = defineGridFilterOperator<LabelFilterOption[] | null>({
  InputComponent: (props: GridFilterInputValueProps) => <LabelAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsLabelFilterItem(filterItem)
    )
      return null;

    const filterLabelIds = new Set(
      filterItem.value.flatMap(filterLabel => {
        return filterLabel.id ? [filterLabel.id] : [];
      })
    );

    return value => {
      // Labels autocomplete is empty.  All rows match.  Order matters, put this at the top.
      if (filterLabelIds.size === 0) return true;
      // We assume params.value is an array of Labels...
      return (value ?? []).some(cellLabel => {
        const labelId = getObjectId(cellLabel);
        return Boolean(labelId && filterLabelIds.has(labelId));
      });
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.Label,
});

export const labelIsEmptyFilterOperator = defineGridFilterOperator<LabelFilterOption[] | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // No labels in cell.
      return (value ?? []).length === 0;
    };
  },
  label: 'is empty',
  value: CustomOperatorValue.LabelIsEmpty,
});

export const labelIsNotEmptyFilterOperator = defineGridFilterOperator<LabelFilterOption[] | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // Some labels in cell.
      return (value ?? []).length > 0;
    };
  },
  label: 'is not empty',
  value: CustomOperatorValue.LabelIsNotEmpty,
});

export const labelNotAnyOfFilterOperator = defineGridFilterOperator<LabelFilterOption[] | null>({
  InputComponent: (props: GridFilterInputValueProps) => <LabelAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsLabelFilterItem(filterItem)
    )
      return null;

    const filterLabelIds = new Set(
      filterItem.value.flatMap(filterLabel => {
        return filterLabel.id ? [filterLabel.id] : [];
      })
    );

    return value => {
      if (filterLabelIds.size === 0) return true;

      const cellHasSomeLabel = (value ?? []).some(cellLabel => {
        const labelId = getObjectId(cellLabel);
        return Boolean(labelId && filterLabelIds.has(labelId));
      });

      return !cellHasSomeLabel;
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.LabelNotAnyOf,
});

// The order of these operators is what shows up in the UI.
export const allLabelFilters = [
  labelAnyOfFilterOperator,
  labelNotAnyOfFilterOperator,
  labelIsEmptyFilterOperator,
  labelIsNotEmptyFilterOperator,
];

interface WorkflowStatusFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.WorkflowStatus;
  value?: Apollo.WorkflowStatus[];
}

function getIsWorkflowStatusFilterItem(item: GridFilterItem): item is WorkflowStatusFilterItem {
  return item.operator === CustomOperatorValue.WorkflowStatus;
}

export const workflowStatusAnyOfFilterOperator =
  defineGridFilterOperator<Apollo.WorkflowStatus | null>({
    InputComponent: (props: GridFilterInputValueProps) => (
      <WorkflowStatusAutocompleteFilter {...props} />
    ),
    getApplyFilterFn: filterItem => {
      if (
        !filterItem.field ||
        !filterItem.value ||
        !filterItem.operator ||
        !getIsWorkflowStatusFilterItem(filterItem)
      )
        return null;

      const filterItemStatusList = new Set(filterItem.value);

      return value => {
        // WorkflowStatus autocomplete is empty.  All rows match.
        if (filterItemStatusList.size === 0) return true;

        // We assume params.value is WorkflowStatus...
        return value ? filterItemStatusList.has(value) : false;
      };
    },
    label: 'is any of',
    value: CustomOperatorValue.WorkflowStatus,
  });

export interface RowStatusFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.RowStatusAnyOf | CustomOperatorValue.RowStatusNotAnyOf;
  value?: Apollo.RowStatus[];
}

export function getIsRowStatusFilterItem(item: GridFilterItem): item is RowStatusFilterItem {
  return (
    item.operator === CustomOperatorValue.RowStatusAnyOf ||
    item.operator === CustomOperatorValue.RowStatusNotAnyOf
  );
}

export const rowStatusAnyOfFilterOperator = defineGridFilterOperator<Apollo.RowStatus | null>({
  InputComponent: (props: GridFilterInputValueProps) => <RowStatusAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsRowStatusFilterItem(filterItem)
    )
      return null;

    const filterItemStatusList = new Set(filterItem.value);

    return value => {
      // RowStatus autocomplete is empty.  All rows match.
      if (filterItemStatusList.size === 0) return true;

      // We assume params.value is RowStatus...
      return value ? filterItemStatusList.has(value) : false;
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.RowStatusAnyOf,
});

export const rowStatusNotAnyOfFilterOperator = defineGridFilterOperator<Apollo.RowStatus | null>({
  InputComponent: (props: GridFilterInputValueProps) => <RowStatusAutocompleteFilter {...props} />,
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsRowStatusFilterItem(filterItem)
    )
      return null;

    const filterItemStatusList = new Set(filterItem.value);

    return value => {
      // RowStatus autocomplete is empty.  All rows match.
      if (filterItemStatusList.size === 0) return true;

      // We assume params.value is RowStatus...
      return value ? !filterItemStatusList.has(value) : true;
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.RowStatusNotAnyOf,
});

// The order of these operators is what shows up in the UI.
export const allRowStatusFilters = [rowStatusAnyOfFilterOperator, rowStatusNotAnyOfFilterOperator];

export interface DocumentStatusFilterItem extends GridFilterItem {
  operator: CustomOperatorValue.DocumentStatus;
  value?: Apollo.DocumentStatus[];
}

export function getIsDocumentStatusFilterItem(
  item?: GridFilterItem | null
): item is DocumentStatusFilterItem {
  return item?.operator === CustomOperatorValue.DocumentStatus;
}

export const documentStatusFilterOperator = defineGridFilterOperator<Apollo.DocumentStatus | null>({
  InputComponent: (props: GridFilterInputValueProps) => (
    <DocumentStatusFilterAutoComplete {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsDocumentStatusFilterItem(filterItem)
    )
      return null;

    const filterItemStatusList = new Set(filterItem.value);

    return value => {
      // DocumentStatus autocomplete is empty.  All rows match.
      if (filterItemStatusList.size === 0) return true;

      // We assume params.value is DocumentStatus...
      return filterItemStatusList.has(getDocumentStatusValue(value));
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.DocumentStatus,
});

/**
 * Documents where Aline AI found a signature on the latest version. Offered on
 * the Status column of server-filtered grids only: the row carries no
 * detection result, so there is nothing to match on the client and the
 * server's filter model does the work.
 */
export const signatureDetectedFilterOperator =
  defineGridFilterOperator<Apollo.DocumentStatus | null>({
    InputComponent: () => null,
    getApplyFilterFn: () => null,
    label: 'AI detected signature',
    /**
     * Takes no value. Without this the grid treats the empty item as half
     * filled in and drops it whenever the filter model is rebuilt (opening the
     * panel, adding another filter), so the filter switches itself off.
     */
    requiresFilterValue: false,
    value: CustomOperatorValue.SignatureDetected,
  });

interface WorkflowAssigneeFilterItem extends GridFilterItem {
  operator:
    | CustomOperatorValue.WorkflowAssigneeAnyOf
    | CustomOperatorValue.WorkflowAssigneeNotAnyOf
    | CustomOperatorValue.WorkflowAssigneeIsEmpty
    | CustomOperatorValue.WorkflowAssigneeIsNotEmpty;
  value?: WorkflowAssigneeFilterOption[];
}

export function getIsWorkflowAssigneeFilterItem(
  item: GridFilterItem
): item is WorkflowAssigneeFilterItem {
  return (
    item.operator === CustomOperatorValue.WorkflowAssigneeAnyOf ||
    item.operator === CustomOperatorValue.WorkflowAssigneeNotAnyOf ||
    item.operator === CustomOperatorValue.WorkflowAssigneeIsEmpty ||
    item.operator === CustomOperatorValue.WorkflowAssigneeIsNotEmpty
  );
}

export const workflowAssigneeAnyOfFilterOperator = defineGridFilterOperator<
  WorkflowAssigneeCellValue[] | null
>({
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowAssigneeAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !Array.isArray(filterItem.value) ||
      !filterItem.operator ||
      !getIsWorkflowAssigneeFilterItem(filterItem)
    )
      return null;

    const filterWorkflowAssigneeIds = new Set(
      filterItem.value.flatMap(workflowAssignee => {
        return workflowAssignee.id ? [workflowAssignee.id] : [];
      })
    );

    return value => {
      // Owner autocomplete is empty.  All rows match.  Order matters, put at the top.
      if (filterWorkflowAssigneeIds.size === 0) return true;
      //The value will be an array of objects, each with an account property.
      return (value ?? []).some(assignee =>
        getIsMatchingWorkflowAssignee(assignee, filterWorkflowAssigneeIds)
      );
    };
  },
  label: 'is any of',
  value: CustomOperatorValue.WorkflowAssigneeAnyOf,
});

export const workflowAssigneeIsEmptyFilterOperator = defineGridFilterOperator<
  WorkflowAssigneeCellValue[] | null
>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // No assignees in cell.
      return (value ?? []).length === 0;
    };
  },
  label: 'is empty',
  value: CustomOperatorValue.WorkflowAssigneeIsEmpty,
});

export const workflowAssigneeIsNotEmptyFilterOperator = defineGridFilterOperator<
  WorkflowAssigneeCellValue[] | null
>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => {
      // Some assignees in cell.
      return (value ?? []).length > 0;
    };
  },
  label: 'is not empty',
  value: CustomOperatorValue.WorkflowAssigneeIsNotEmpty,
});

export const workflowAssigneeNotAnyOfFilterOperator = defineGridFilterOperator<
  WorkflowAssigneeCellValue[] | null
>({
  InputComponent: (props: GridFilterInputValueProps) => (
    <WorkflowAssigneeAutocompleteFilter {...props} />
  ),
  getApplyFilterFn: filterItem => {
    if (
      !filterItem.field ||
      !filterItem.value ||
      !filterItem.operator ||
      !getIsWorkflowAssigneeFilterItem(filterItem)
    )
      return null;

    const filterWorkflowAssigneeIds = new Set(
      filterItem.value.flatMap(workflowAssignee => {
        return workflowAssignee.id ? [workflowAssignee.id] : [];
      })
    );

    return value => {
      // Owner autocomplete is empty.  All rows match.  Order matters, put at the top.
      if (filterWorkflowAssigneeIds.size === 0) return true;
      //The value will be an array of objects, each with an account property.

      const cellHasSomeAssignee = (value ?? []).some(assignee =>
        getIsMatchingWorkflowAssignee(assignee, filterWorkflowAssigneeIds)
      );

      return !cellHasSomeAssignee;
    };
  },
  label: 'is none of',
  value: CustomOperatorValue.WorkflowAssigneeNotAnyOf,
});

// The order of these operators is what shows up in the UI.
export const allWorkflowAssigneeFilters = [
  workflowAssigneeAnyOfFilterOperator,
  workflowAssigneeNotAnyOfFilterOperator,
  workflowAssigneeIsEmptyFilterOperator,
  workflowAssigneeIsNotEmptyFilterOperator,
];

// Boolean filter operators
export const booleanIsFilterOperator = defineGridFilterOperator<boolean | null>({
  getApplyFilterFn: filterItem => {
    if (filterItem.value === undefined || filterItem.value === null) return null;
    const filterValue = filterItem.value === 'true' || filterItem.value === true;
    return value => value === filterValue;
  },
  label: 'is',
  value: 'is',
});

export const booleanIsNotFilterOperator = defineGridFilterOperator<boolean | null>({
  getApplyFilterFn: filterItem => {
    if (filterItem.value === undefined || filterItem.value === null) return null;
    const filterValue = filterItem.value === 'true' || filterItem.value === true;
    return value => value !== filterValue;
  },
  label: 'is not',
  value: 'isNot',
});

export const booleanIsEmptyFilterOperator = defineGridFilterOperator<boolean | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => value === null || value === undefined;
  },
  label: 'is empty',
  value: CustomOperatorValue.BooleanIsEmpty,
});

export const booleanIsNotEmptyFilterOperator = defineGridFilterOperator<boolean | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => value !== null && value !== undefined;
  },
  label: 'is not empty',
  value: CustomOperatorValue.BooleanIsNotEmpty,
});

// The order of these operators is what shows up in the UI.
export const allBooleanFilters = [
  booleanIsFilterOperator,
  booleanIsNotFilterOperator,
  booleanIsEmptyFilterOperator,
  booleanIsNotEmptyFilterOperator,
];

function getNormalizedSelectQuestionValues(value: unknown) {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  if (typeof value === 'string' && value) return [value];
  return [];
}

function getNormalizedSelectQuestionCellValue(value: unknown) {
  if (typeof value !== 'string') return '';
  return value.toLowerCase();
}

export const questionSelectNotAnyOfFilterOperator = defineGridFilterOperator<string | null>({
  getApplyFilterFn: filterItem => {
    const filterValues = getNormalizedSelectQuestionValues(filterItem.value).map(value =>
      value.toLowerCase()
    );
    if (!filterValues.length) return null;

    return value => !filterValues.includes(getNormalizedSelectQuestionCellValue(value));
  },
  label: 'is none of',
  value: CustomOperatorValue.QuestionSelectNotAnyOf,
});

export const questionSelectIsAnyOfFilterOperator = defineGridFilterOperator<string | null>({
  getApplyFilterFn: filterItem => {
    const filterValues = getNormalizedSelectQuestionValues(filterItem.value).map(value =>
      value.toLowerCase()
    );
    if (!filterValues.length) return null;

    return value => filterValues.includes(getNormalizedSelectQuestionCellValue(value));
  },
  label: 'is any of',
  value: 'isAnyOf',
});

export const questionSelectIsEmptyFilterOperator = defineGridFilterOperator<string | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => !value;
  },
  label: 'is empty',
  value: 'isEmpty',
});

export const questionSelectIsNotEmptyFilterOperator = defineGridFilterOperator<string | null>({
  InputComponent: () => null,
  getApplyFilterFn: () => {
    return value => Boolean(value);
  },
  label: 'is not empty',
  value: 'isNotEmpty',
});

// The order of these operators is what shows up in the UI.
export const allQuestionSelectFilters = [
  questionSelectIsAnyOfFilterOperator,
  questionSelectNotAnyOfFilterOperator,
  questionSelectIsEmptyFilterOperator,
  questionSelectIsNotEmptyFilterOperator,
];

/**
 * Returns string filter operators with a safe isAnyOf operator.
 * In MUI DataGrid v8, the default isAnyOf operator's InputComponent (GridFilterInputMultipleValue)
 * expects item.value to be an array and will crash if it's undefined/null.
 * This function wraps the InputComponent to ensure the value is always an array.
 */
export function getStringOperatorsWithSafeIsAnyOf(): GridFilterOperator[] {
  const operators = getGridStringOperators();

  return operators.map(op => {
    if (op.value !== 'isAnyOf') return op;

    const OriginalInputComponent = op.InputComponent;
    if (!OriginalInputComponent) return op;

    // Wrap the InputComponent to ensure value is always an array
    const SafeInputComponent = (props: GridFilterInputValueProps) => {
      const safeItem = {
        ...props.item,
        value: Array.isArray(props.item.value) ? props.item.value : [],
      };

      return <OriginalInputComponent {...props} item={safeItem} />;
    };

    return {
      ...op,
      InputComponent: SafeInputComponent,
    };
  });
}
